import * as React from "react";

import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "~/lib/utils";

import { H5, P, Label } from "./typography";
// AIDEV-NOTE: `info` / `warning` / `success` / `error` variants replace the design
// bundle's "CalloutCard" pattern — flow layout, no auto icon positioning.
// Legacy `default` / `destructive` variants keep the old absolute-positioned-svg
// layout for backward compatibility with existing callsites (size-4 svg child).
// New variants render any child (including svg) inline; consumers control layout.
const alertVariants = cva("relative w-full rounded-xl border p-4", {
    variants: {
        variant: {
            default:
                "bg-background text-foreground [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground",
            destructive:
                "border-destructive/50 text-destructive dark:border-destructive [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-destructive",
            // AIDEV-NOTE: design-system callout variants are text-only by spec.
            // Direct <svg> children are hidden so consumers can pass lucide icons
            // (e.g. in the showcase) without breaking the visual. To use an icon,
            // wrap it in a <div> so it renders inline alongside title/description.
            info: "border-brand-forest-200 bg-brand-forest-50 text-brand-forest-800 [&>svg]:hidden",
            warning:
                "border-[#E3B13E] bg-[#FCF3D8] text-brand-forest-800 [&>svg]:hidden",
            success:
                "border-brand-forest-300 bg-brand-forest-100 text-brand-forest-800 [&>svg]:hidden",
            error: "border-[#973D2A] bg-[#FBE4DE] text-brand-forest-800 [&>svg]:hidden",
        },
    },
    defaultVariants: {
        variant: "default",
    },
});

const Alert = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, ...props }, ref) => (
    <div
        ref={ref}
        role="alert"
        className={cn(alertVariants({ variant }), className)}
        {...props}
    />
));
Alert.displayName = "Alert";

const AlertTitle = React.forwardRef<
    HTMLHeadingElement,
    React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
    <H5
        ref={ref}
        className={cn("mb-1 mt-0 font-medium tracking-tight", className)}
        {...props}
    />
));
AlertTitle.displayName = "AlertTitle";

const AlertDescription = React.forwardRef<
    HTMLLabelElement,
    React.HTMLAttributes<HTMLLabelElement>
>(({ className, ...props }, ref) => (
    <Label
        ref={ref}
        className={cn("leading-relaxed mt-0", className)}
        {...props}
    />
));
AlertDescription.displayName = "AlertDescription";

export { Alert, AlertTitle, AlertDescription };
